/**
 * Appearance (Figma 09 · Form pages / 09, client C06). Kept on this device:
 * the backend stores no preferences, so it cannot follow you to other devices.
 */
import { useAppearanceStore, type ThemePreference } from "@/stores/appearanceStore";
import { KitSegmentedControl } from "../../kit/SegmentedControl";
import { KitSwitch } from "../../kit/Switch";
import { Card, PageHead, QuietNote, SectionHead, SettingsPage } from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";

const THEMES: { label: string; value: ThemePreference }[] = [
  { label: "System", value: "system" },
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
];

function ComfortRow({
  title,
  body,
  checked,
  onChange,
}: {
  title: string;
  body: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex w-full items-center gap-4 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-[14px] font-semibold text-co-text">{title}</p>
        <p className="text-[12.5px] leading-[1.45] text-co-muted">{body}</p>
      </div>
      <KitSwitch checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

export default function AppearancePage() {
  const head = useYouHead();
  const { theme, reduceMotion, solidSurfaces, setTheme, setReduceMotion, setSolidSurfaces } = useAppearanceStore();

  return (
    <SettingsPage>
      <PageHead {...head} title="Appearance" status="How Creo looks for you on this device." />
      <Card>
        <SectionHead title="Theme" />
        <KitSegmentedControl options={THEMES} value={theme} onChange={setTheme} ariaLabel="Theme" className="self-start" />
        <QuietNote>System follows your device, light by day and dark by night if your device does that.</QuietNote>

        <SectionHead title="Comfort" />
        <div className="flex flex-col">
          <ComfortRow
            title="Reduce motion"
            body="Fewer moving transitions. Waiting shows as still text."
            checked={reduceMotion}
            onChange={setReduceMotion}
          />
          <ComfortRow
            title="Solid surfaces"
            body="Bars and panels without the glass blur, for more contrast"
            checked={solidSurfaces}
            onChange={setSolidSurfaces}
          />
        </div>
      </Card>
    </SettingsPage>
  );
}
